/**
 * @fileoverview Main order base component serving as the primary interface for order management.
 * Orchestrates the complete order management system including data grid display, filtering,
 * real-time collaboration, vendor-specific logic, and comprehensive order editing capabilities.
 *
 * This is the central component that integrates all order management features including:
 * - Multi-vendor support (Vivid, TicketNetwork, GoTickets, Resale)
 * - Real-time collaborative editing with Socket.IO
 * - Advanced filtering and sorting capabilities
 * - Status management and workflow processing
 * - Tag management and order notes
 * - Expandable detail views for comprehensive editing
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

/* eslint-disable no-unused-vars */
/* eslint-disable eqeqeq */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import RaList from '../../../components/ra-list/RaList';
import RenderControls from '../../../components/ra-list/RenderControls';
import { useNotify, useStore } from 'react-admin';
import { dataProvider } from '../../../DataProvider';
import { getDefaultUtcOffset, loadSourceType } from '../../../utils/common';
import { DeliveryMethodEnum, Shipping, getTagsColor } from '../../../utils/enum';
import './css/style.css';
import { useColorScheme, LinearProgress } from '@mui/material';
import { historicalTags, bus_events } from './vendor';
import { initSocketIO as createUI } from '../lib/uiHighlighter';
import OrderGrid from './component/order-grid';
import { COLLAB_HOST } from '../../../serviceUrls';

interface IData {
    [key: string]: any;
}

/**
 * Main order base component providing comprehensive order management functionality.
 * Serves as the primary interface for displaying, filtering, and managing orders across
 * multiple vendor systems with real-time collaboration capabilities.
 *
 * @param {Object} props - Component properties
 * @param {Object} props.vendor - Vendor-specific configuration and business logic
 * ... another props
 * @returns {JSX.Element} Complete order management interface
 */
const OrderBase = (props: IData) => {
    const { vendor } = props;
    const { mode } = useColorScheme();
    const [expandedIds, setExpandedId] = useStore<any[]>(`${vendor.controller}.datagrid.expanded`, []);//get ids of expanded row by store name

    const refFilter = useRef<any>(null);
    const [sortBy, setSortBy] = useState(['order_number desc']);
    const [defaultValueFilter, setDefaultValueFilter] = useState<IData>({
        exclude_eq: null,
        utc_offset_eq: getDefaultUtcOffset(),
        // order_date: ['01/11/2026', '03/11/2026'],
        // order_number_eq: 78336832 // VV
        // order_number_eq: 52511525 // TN
    });

    const [isLoading, setIsLoading] = useState(false);
    const [filterConfig, setFilterConfig] = useState<IData>({ disabledAll: false, firstName: null });
    const [orderAccounts, setOrderAccounts] = useState<any[]>([]);
    const [marketType, setMarketType] = useState<any[]>([]);
    const [assignedTo, setAssignedTo] = useState<any[]>([]);
    const [allowStatuses] = useState(vendor.getStatuses());
    const [tags, setTags] = useState(vendor.getTags());
    const [appIO, setAppIO] = useState<IData>({ bus: {}, specificVendor: null, options: {}, bus_events: bus_events, registedBus: false });
    const [editingOrders, setEditingOrders] = useState<any[]>([]);
    const editingOrdersRef = useRef(editingOrders);
    const [effectData, setEffectData] = useState<IData>({
        isAlert: false,
        showOrderLastChangedBy: undefined,
        currentOrder: null,
        notes: null
    });
    const [showSystemNotes, setShowSystemNotes] = useState(false);
    const isResale = vendor.controller == 'Resale';
    const usesStrOrderNumber = vendor.checkIfUseStrOrderNumber();
    const notify = useNotify();

    const excluded = useMemo(() => [
        { name: 'exclude_ht', label: 'Hard Tickets' },
        { name: 'exclude_wc', label: 'Will Call' },
        { name: 'exclude_is', label: 'IsSpec', hidden: !isResale, defaultValue: false },
        { name: 'exclude_usi', label: 'Unsold inventory', hidden: !isResale, defaultValue: true },
        { name: 'exclude_pp', label: 'Postponed' },
        { name: 'exclude_ntr', label: 'Needs to be refunded' },
        { name: 'exclude_wts', label: 'Wait To Ship' },
        { name: 'exclude_deli', label: 'Delivery' },
        { name: 'exclude_dnp', label: 'Do Not Process' },
        { name: 'exclude_aea', label: 'Awaiting Exchange Action' },
        { name: 'exclude_pvr', label: 'Pending Venue Response' },
        { name: 'exclude_osp', label: 'Order Support' },
        { name: 'exclude_cbd', label: 'Checkback date' }
    ], [isResale]);

    React.useEffect(() => {
        if (isResale) setSortBy(['order_date desc']);
    }, [isResale]);

    // Update ref whenever editingOrders changes
    React.useEffect(() => {
        editingOrdersRef.current = editingOrders;
    }, [editingOrders]);

    React.useEffect(() => {
        setIsLoading(true);
        // Loading current user | assignedTo
        dataProvider.currentUser().then((user: any) => {
            if (user.email) {
                setFilterConfig(prev => ({ ...prev, currUser: user.email, firstName: user.id }));
                dataProvider.fetchData(vendor.controller, `GetUsersWorkingToday?email=${user.email}&utcOffset=${defaultValueFilter.utc_offset_eq}`)
                    .then((response: any) => {
                        if (response.status !== 200 && response.data) {
                            let sample = [{ id: '-1', name: 'ANY' }, { id: '0', name: 'General' }];
                            let arr = sample.concat(response.data.map((x: any) => ({ id: x.email + '', name: x.aliasName })));
                            setAssignedTo(arr);
                        }
                    })
                    .catch(console.error);
            }
        });

        // Loading order accounts
        dataProvider
            .fetchData(vendor.controller, 'GetOrderAccounts')
            .then((response: any) => {
                if (response.status !== 200 && response.data) {
                    let arr = response.data.map((x: any) => ({ id: x.id, name: x.spec_alias }));
                    setOrderAccounts(arr);
                }
            })
            .catch(console.error);
        // Loading market type
        loadSourceType(dataProvider, res => setMarketType(res), false);

        //load order tags
        dataProvider.fetchData(vendor.controller, 'GetOrderTags')
            .then((response: any) => {
                if (response.status !== 200 && response.data) {
                    response.data.forEach((tag) => {
                        if (!historicalTags.some(ht => ht.toLowerCase() == tag.toLowerCase()) && !tags.some(t => t.toLowerCase() == tag.toLowerCase())) {
                            tags.push(tag);
                        }
                    });
                    setTags(tags);
                } else {
                    console.error('Load Order Tags Failed');
                }
            })
            .catch(console.error);
    }, []);

    function RegisterIOBus () {
        //New IO-Highlight
        let bus = vendor.bus;

        //#region New-IO
        const IO_Options = {
            options: {
                ioUrl: COLLAB_HOST,
                // ioUrl: 'localhost:8083',
                env: {
                    Identity: filterConfig.currUser,
                    FirstName: filterConfig.firstName ?? 'Toan'
                }
            },
            bus_events: bus_events,
            bus: bus,
            vendor: vendor,
            setEditingOrders: setEditingOrders
        };

        setAppIO(IO_Options);
        createUI(IO_Options);

        //#endregion

        //Grid-bus
        const createGridBus = () => {
            //#region Grid-bus
            bus.on(bus_events.order_saved, (data) => updateGridRow(data, 'order_saved'));
            bus.on(bus_events.order_note_saved, (data) => updateGridRow(data, 'order_note_saved'));
            bus.on(bus_events.order_fetch_newdata, (data) => updateGridRow(data, 'order_fetch_newdata'));// Only update data on grid row => avoid calling alert for saving
            bus.on(bus_events.grid_order_shown, (data) => updateGridRowByOrder(data, 'grid_order_shown'));
            //#endregion

            function updateGridRowByOrder (order, prefix) {
                // console.log(`*** updateGridRowByOrder`);
                let notes = order.order_notes ?? [];
                order.order_notes = notes.reduce(function (prev: any, cur: any) { if (prev.length < 3 && cur.note_type == 0) prev.push(cur); return prev; }, []);
                //order.order_fullnotes = notes;

                const TARGET_PREFIXES_FOR_UPDATE = ['order_saved', 'order_fetch_newdata'];

                if (TARGET_PREFIXES_FOR_UPDATE.includes(prefix)) {
                    setEditingOrders(prev => {
                        return prev.map((item: any) => {
                            if (matchesOrderNumber(item, order.order_number)) {
                                return { ...item, rowData: order };
                            }
                            return item;
                        });
                    });
                }
                // bus.emit(bus_events.refresh_highlight_box, order, prefix);
            }

            function updateGridRow (data, prefix) {
                // console.log(`*** updateGridRow - ${prefix}`);
                const order = parseOrder(data);
                updateGridRowByOrder(order, prefix);
            }
        };

        bus.on(bus_events.order_status_changed, (data) => {
            console.log(`*** Grid order_status_changed - ${data.order_number}`, data);
        });

        const parseOrder = (data) => Array.isArray(data) ? data.reduce((prev: any, { name, value }: any) =>
            ({ ...prev, [name]: name === 'internal_status' ? parseInt(value) : value }), {})
            : typeof data === 'object' && data !== null ? data : {};

        const handleOrderStatusChanged = (data) => {
            let orderNumber = data.order_number ?? data.orderNumber;
            if (!!orderNumber) {
                let editOrders = editingOrdersRef.current?.filter((x: any) => matchesOrderNumber(x, orderNumber)).sort((a: any, b: any) => {
                    // Sort by timestamp, most recent first
                    return new Date(b.timeStamp) - new Date(a.timeStamp);
                });
                let editOrder = editOrders.length > 0 ? editOrders[0] : null;
                if (!editOrder) return;

                // console.log(`*** 1.order_status_changed - ${orderNumber} (status changed)`, data, editOrder);
                let newStatus = parseInt(data.newStatus ?? data.internal_status);
                if (!Number.isNaN(newStatus) && newStatus !== parseInt(editOrder?.status)) {
                    // console.log(`*** 2.order_status_changed - ${orderNumber} (status changed)`, data, editOrder);
                    editOrder.status = newStatus; // Update editOrder status to match data
                    //Apply for revert status: losing focus on editingOrders
                    setEditingOrders(prev => {
                        let index = prev.findIndex(x => matchesOrderNumber(x, editOrder.orderNumber));
                        if (index !== -1) {
                            if (!editOrder.rowData) editOrder.rowData = {}; // Ensure rowData exists
                            editOrder.rowData.internal_status = newStatus; // Update rowData with new status
                            prev[index] = editOrder;
                        }
                        return [...prev];
                    });
                    return true;
                }
            }
            return false;
        };

        //Editor-bus
        const createEditorBus = () => {
            console.log('Need to register editor-bus');
            //#region BUS

            //this event was fired before the dom removed, that keeps the datetime pickers and other object attached
            bus.on(bus_events.grid_selection_changed, function (data) {
                setEffectData((prev) => ({ ...prev, isAlert: false, currentOrder: data }));
                console.log('grid_selection_changed', data);
            });

            bus.on(bus_events.order_saved, function (data, by) {
                let order = parseOrder(data);
                let id = order.ID ?? order.id; //Combine 2 versions

                showAlert(order, id, by);
            });

            bus.on(bus_events.order_note_saved, function (data, by) {
                // console.log(`order_note_saved`, data, by);
                let order = parseOrder(data);
                let orderId = window.currentOrder?.id;
                let id = order.ID ?? order.id; //Combine 2 versions
                if (orderId == id)
                    setEffectData((prev) => ({ ...prev, isAlert: false, notes: order.order_fullnotes }));
            });

            bus.on(bus_events.order_status_changed, (data) => {
                handleOrderStatusChanged(data);
            });

            //#endregion

            function showAlert (data, id, by) {
                const orderId = window.currentOrder?.id;
                if (orderId != id) return;

                setEffectData(prev => ({
                    ...prev,
                    currentOrder: data,
                    showOrderLastChangedBy: by,
                    isAlert: filterConfig.currUser !== by
                }));
            }
        };

        createGridBus();
        createEditorBus();
    }

    React.useEffect(() => {
        if (filterConfig.currUser != null) {
            RegisterIOBus();
            setIsLoading(false);
        }
    }, [filterConfig.currUser]);

    useEffect(() => {
        if (defaultValueFilter.order_number_eq) setFilterConfig(prev => ({ ...prev, disabledAll: true }));
    }, [defaultValueFilter]);

    const handleOrderNumberFilter = (props) => {
        if (props.value) setFilterConfig(prev => ({ ...prev, disabledAll: true }));
        else setFilterConfig(prev => ({ ...prev, disabledAll: false }));
    };

    const handleChangeInternalStatus = (selected) => {
        if (selected.value == 'Unprocessed') {
            setDefaultValueFilter((prev) => ({ ...prev, exclude_eq: getGroupChkBoxFormatter(['exclude_dnp']) }));
        } else {
            setDefaultValueFilter((prev) => ({ ...prev, exclude_eq: getGroupChkBoxFormatter(['exclude_dnp'], false) }));
        }
    };

    function getGroupChkBoxFormatter (values = [], value = true) {
        return values.map(((x: any) => ({ name: x, value: value })));
    }

    const matchesOrderNumber = (editingOrder, orderNumber) => {
        if (!orderNumber) return false;
        return editingOrder.orderNumber == orderNumber || editingOrder.orderNumber?.order_number == orderNumber;
    };

    const controls = [
        { component: usesStrOrderNumber ? 'text' : 'number', name: usesStrOrderNumber ? 'str_order_number_eq' : 'order_number_eq', fullWidth: false, xs: 1, label: 'Order #', onChange: handleOrderNumberFilter },
        { component: 'select', className: 'mselect-small', name: 'vivid_account_id_eq', label: 'Account', choices: orderAccounts || [], disabled: filterConfig?.disabledAll },
        { component: 'select', className: 'mselect-small', name: 'internal_status_eq', label: 'Status', choices: allowStatuses || [], xs: 2, disabled: filterConfig?.disabledAll, onChange: handleChangeInternalStatus },
        { component: 'daterange', name: 'order_date', label: 'Order Date', xs: 2, disabled: filterConfig?.disabledAll },
        { component: 'daterange', name: 'date', label: 'Event Date', xs: 2, disabled: filterConfig?.disabledAll },
        { component: 'text', name: 'notes', label: 'Order Notes', xs: 3.5, disabled: filterConfig?.disabledAll },
        { component: 'text', name: 'event', label: 'Event', xs: 2, disabled: filterConfig?.disabledAll },
        { component: 'text', name: 'venue', label: 'Venue', xs: 2, disabled: filterConfig?.disabledAll },
        { component: 'select', className: 'mselect-small', name: 'delivery_method_eq', label: 'Delivery Method', choices: DeliveryMethodEnum.map((x: any) => ({ ...x, id: x.value })) || [], disabled: filterConfig?.disabledAll },
        { component: 'select', className: 'mselect-small', name: 'event_market_eq', label: 'Market Type', choices: marketType || [], disabled: filterConfig?.disabledAll },
        { component: 'autocomplete', id: 'mautocomplete', name: 'tags_eq', label: 'Tags', choices: tags || [], getStyles: getTagsColor, disabled: filterConfig?.disabledAll, variant: 'filled' },
        { component: 'checkboxgroup', name: 'exclude_eq', className: 'chk-group-excludes', data: excluded, label: 'Exclude', xs: 4, disabled: filterConfig?.disabledAll },
        { component: 'daterange', name: 'checkback_date', label: 'Check back date', disabled: filterConfig?.disabledAll },
        { component: 'daterange', name: 'in_hand_date', label: 'Inhand Date', disabled: filterConfig?.disabledAll },
        { component: 'select', className: 'mselect-small', name: 'shipping', label: 'Shipping', choices: Shipping, xs: 1, isHide: vendor.hideShippingFilter(), disabled: filterConfig?.disabledAll },
        { component: 'select', className: 'mselect-small', name: 'assigned_to_eq', label: 'Assigned To', choices: assignedTo || [], xs: vendor.hideShippingFilter() ? 2 : 1, disabled: filterConfig?.disabledAll },
        { component: 'text', name: 'primary_order_number_contains', label: 'Primary Order Number', placeholder: '10-32454/NCA', disabled: filterConfig?.disabledAll },
        { component: 'text', name: 'primary_account_contains', label: 'Primary Account', placeholder: 'jennfer.lynn4926@gmail.com', disabled: filterConfig?.disabledAll },
        { component: 'text', name: 'utc_offset_eq', isHide: true },
    ];

    if (isLoading) {
        return <LinearProgress color='secondary' sx={{ marginBottom: 1 }} />;
    }

    return (
        <div id='order-page' className={`theme-${mode}`}>
            <RaList
                title={vendor.getTitle()}
                sortby={sortBy}
                perPage={50}
                pagination={[50, 100, 200, 500, 1000]}
                filters={
                    <RenderControls
                        refFilter={refFilter}
                        controls={controls}
                        defaultValues={defaultValueFilter}
                        beforeSearch={() => {
                            setExpandedId([]);
                            appIO.bus.emit && appIO.bus.emit(bus_events.grid_before_send);
                        }}
                        onLoadCompleted={() => {
                            appIO.bus.emit && appIO.bus.emit(bus_events.grid_completed);
                        }}
                    />
                }
            >
                <OrderGrid
                    orderAccounts={orderAccounts}
                    filterConfig={filterConfig}
                    appIO={appIO}
                    editingOrders={editingOrders}
                    setEditingOrders={setEditingOrders}
                    matchesOrderNumber={matchesOrderNumber}
                    effectData={effectData}
                    setEffectData={setEffectData}
                    isResale={isResale}
                    usesStrOrderNumber={usesStrOrderNumber}
                    showSystemNotes={showSystemNotes}
                    setShowSystemNotes={setShowSystemNotes}
                    marketType={marketType}
                    tags={tags}
                    mode={mode} // for configRowUI
                    notify={notify}
                    expandedIds={expandedIds}
                    setExpandedId={setExpandedId}
                />
            </RaList >
        </div>
    );
};

export default OrderBase;
